import { supabase } from '../lib/supabase';

async function ensureCustomer({ name, phone, address, upazila, district }) {
  // Try to find existing customer by phone
  const { data: existing, error: findError } = await supabase
    .from('customers')
    .select('*')
    .eq('phone', phone)
    .maybeSingle();
  if (findError) throw findError;
  
  if (existing) {
    // Update existing customer with new details if they've changed
    const needsUpdate = existing.name !== name || 
                       existing.address !== address || 
                       existing.upazila !== upazila || 
                       existing.district !== district;
    
    if (needsUpdate) {
      const { data: updated, error: updateError } = await supabase
        .from('customers')
        .update({ name, address, upazila, district })
        .eq('id', existing.id)
        .select()
        .single();
      if (updateError) throw updateError;
      return updated;
    }
    
    return existing;
  }

  // Create new customer
  const { data, error } = await supabase
    .from('customers')
    .insert({ name, phone, address, upazila, district })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function createOrder({
  items, // [{ product_id, quantity, unit_price }]
  customer,
  notes,
  verificationCode,
  payment_method,
  payment_transaction_id,
  payment_sender_number,
  delivery_charge = 0,
  transaction_fee = 0,
  total_amount = 0
}) {
  try {
    const customerRow = await ensureCustomer(customer);

    const { data: order, error: orderError } = await supabase
      .from('orders')
      .insert({
        customer_id: customerRow.id,
        status: 'pending',
        payment_status: payment_method === 'cash' ? 'unpaid' : 'pending',
        payment_method: payment_method || null,
        payment_transaction_id: payment_transaction_id || null,
        payment_sender_number: payment_sender_number || null,
        verification_code: verificationCode || null,
        notes: notes || null,
        delivery_charge: delivery_charge || 0,
        transaction_fee: transaction_fee || 0,
        total_amount: total_amount || 0
      })
      .select()
      .single();
    
    if (orderError) throw orderError;

    if (items && items.length > 0) {
      const { error: itemsError } = await supabase
        .from('order_items')
        .insert(items.map((it) => ({ ...it, order_id: order.id })));
      
      if (itemsError) throw itemsError;
    }

    return order;
  } catch (error) {
    console.error('createOrder error:', error);
    throw error;
  }
}

export async function listOrders() {
  const { data, error } = await supabase
    .from('orders')
    .select('*, customers(*), order_items(*, products(*))')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function updateOrderStatus(orderId, status, payment_status = null) {
  const updates = { status };
  if (payment_status !== null) {
    updates.payment_status = payment_status;
  }
  
  // Track delivery date when status changes to 'delivered'
  if (status === 'delivered') {
    updates.delivered_at = new Date().toISOString();
  }
  
  // Track completion date when status changes to 'completed'
  if (status === 'completed') {
    updates.completed_at = new Date().toISOString();
  }
  
  const { data, error } = await supabase
    .from('orders')
    .update(updates)
    .eq('id', orderId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function markOrderAsCompleted(orderId) {
  const { data, error } = await supabase
    .from('orders')
    .update({ 
      status: 'completed',
      completed_at: new Date().toISOString(),
      payment_status: 'paid' // Completed orders should remain paid
    })
    .eq('id', orderId)
    .select()
    .single();
  if (error) throw error;
  return data;
}


